import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";

import {
  listLibrary,
  LibraryError,
  type LibraryItem,
  type ListLibraryResponse,
} from "@/lib/api/library";
import { useAuth } from "@/lib/auth";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

export const Route = createFileRoute("/library/")({
  component: LibraryPage,
});

function LibraryPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 250);

  const query = useInfiniteQuery<
    ListLibraryResponse,
    LibraryError | Error,
    InfiniteData<ListLibraryResponse>,
    string[],
    string | undefined
  >({
    queryKey: ["library", debouncedQ],
    queryFn: ({ pageParam }) => listLibrary({ q: debouncedQ || undefined, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !loading && !!user,
    retry: (failureCount, err) =>
      !(err instanceof LibraryError && err.kind === "unauthorized") && failureCount < 3,
  });

  if (!loading && !user) {
    void navigate({ to: "/login", search: { next: "/library" } });
    return null;
  }

  if (query.error instanceof LibraryError && query.error.kind === "unauthorized") {
    void navigate({ to: "/login", search: { next: "/library" } });
    return null;
  }

  const allItems = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="paper-page" data-testid="library-page">
      <div className="mkt-search-bar">
        <input
          type="search"
          aria-label="search library"
          className="mkt-search-input"
          placeholder="search your purchases"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="mkt-grid-section">
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <h1 style={{ fontSize: 28, margin: 0, fontWeight: 600 }}>your library</h1>
        </div>

        {query.status === "pending" && <SkeletonGrid count={6} />}

        {query.status === "error" && (
          <p role="alert" className="mkt-empty">
            couldn't load your library
          </p>
        )}

        {query.status === "success" && allItems.length === 0 && (
          <p className="mkt-empty">
            {debouncedQ
              ? "no purchases match your search"
              : "you haven't purchased any artifacts yet"}
          </p>
        )}

        {allItems.length > 0 && (
          <div className="mkt-grid" data-testid="library-grid">
            {allItems.map((item) => (
              <a key={item.id} href={`/library/${item.id}`}>
                <LibraryCard item={item} />
              </a>
            ))}
          </div>
        )}

        {query.hasNextPage && (
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <button
              type="button"
              className="mkt-buy-btn"
              onClick={() => {
                void query.fetchNextPage();
              }}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? "loading" : "load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryCard({ item }: { item: LibraryItem }) {
  return (
    <article className="mkt-card">
      <div className="mkt-card-meta">
        <h2 className="mkt-card-title">"{item.title}"</h2>
        <p className="mkt-card-maker">
          by {item.automaton_name}
          {item.automaton_status === "dead" && (
            <span className="mkt-badge-stopped" aria-label="from a stopped automaton">
              {" "}
              · stopped
            </span>
          )}
        </p>
        <p className="mkt-card-summary" style={{ fontSize: 12, color: "#6e7a71", marginBottom: 8 }}>
          {item.summary}
        </p>
        {item.taken_down && (
          <span className="lib-badge-takendown" aria-label="taken down">
            taken down
          </span>
        )}
        <div className="mkt-card-foot">
          <time dateTime={item.purchased_at} className="lib-purchased-at">
            purchased {formatRelative(item.purchased_at)}
          </time>
        </div>
      </div>
    </article>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function SkeletonGrid({ count }: { count: number }) {
  return (
    <div className="mkt-grid" aria-busy="true" aria-label="loading">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="mkt-card"
          style={{ minHeight: 120, opacity: 0.4 }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
